import { useCallback } from "preact/hooks";
import { getItem } from "@/content/items";
import { lt, t } from "@/i18n";
import { addItems, hero, itemCount, panel, showToast } from "@/state/store";
import { useKeyNav } from "./useKeyNav";

const BASE_GOODS = ["potion", "big_potion", "hint_scroll"];
const BRIDGE_GOODS = ["sword_iron", "leather_vest"];

export function shopGoods(): string[] {
  return hero.value.flags["bridge_fixed"] ? [...BASE_GOODS, ...BRIDGE_GOODS] : BASE_GOODS;
}

export function buyItem(itemId: string): boolean {
  const h = hero.value;
  const item = getItem(itemId);
  const price = item.price ?? 0;
  if (h.gold < price) {
    showToast(t("shop.tooExpensive"));
    return false;
  }
  hero.value = addItems({ ...h, gold: h.gold - price }, { [itemId]: 1 });
  showToast(t("shop.bought", { name: lt(item.name) }));
  return true;
}

export function ShopPanel() {
  const h = hero.value;
  const goods = shopGoods();
  const activate = useCallback((i: number) => {
    const id = shopGoods()[i];
    if (id) buyItem(id);
  }, []);
  const selected = useKeyNav({ count: goods.length, onActivate: activate });

  return (
    <div class="shop">
      <div class="shop-top">
        <span class="title">🛒 {t("shop.title")}</span>
        <span class="gold">💰 {t("shop.gold", { gold: h.gold })}</span>
        <button class="close" onClick={() => (panel.value = null)}>
          {t("shop.close")}
        </button>
      </div>
      <div class="goods">
        {goods.map((id, i) => {
          const item = getItem(id);
          const price = item.price ?? 0;
          const owned = itemCount(h, id) + (Object.values(h.equipment).includes(id) ? 1 : 0);
          return (
            <div
              key={id}
              class={`good ${i === selected.value ? "selected" : ""}`}
              onMouseEnter={() => (selected.value = i)}
            >
              <kbd>{i + 1}</kbd>
              <span class="icon">{item.icon}</span>
              <div class="info">
                <div class="name">
                  {lt(item.name)}
                  {owned > 0 && <span class="owned">{t("shop.owned", { n: owned })}</span>}
                </div>
                <div class="desc">{lt(item.description)}</div>
              </div>
              <button
                class={h.gold >= price ? "primary" : "poor"}
                title={h.gold >= price ? undefined : t("shop.tooExpensive")}
                onClick={() => buyItem(id)}
              >
                {t("shop.buy")} · {price} 💰
              </button>
            </div>
          );
        })}
      </div>
      <p class="hint">
        {t("keys.list")}
        {!h.flags["bridge_fixed"] && <> · {t("shop.moreAfterBridge")}</>}
      </p>
    </div>
  );
}
